import { useMemo } from 'react';

interface SchemaProperty {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array';
  title?: string;
  description?: string;
  default?: unknown;
  enum?: Array<string | number | boolean>;
  items?: { type: 'string' | 'number' | 'integer' | 'boolean' };
}

interface SupportedInputSchema {
  type: 'object';
  required?: string[];
  properties: Record<string, SchemaProperty>;
}

interface TemplateDynamicFormProps {
  schema: Record<string, unknown>;
  uiSchema?: Record<string, unknown>;
  values: Record<string, unknown>;
  onChange: (nextValues: Record<string, unknown>) => void;
  disabled?: boolean;
}

export default function TemplateDynamicForm({
  schema,
  uiSchema,
  values,
  onChange,
  disabled = false,
}: TemplateDynamicFormProps) {
  const normalizedSchema = useMemo(() => normalizeSchema(schema), [schema]);

  if (!normalizedSchema) {
    return <p className='text-[0.875rem] text-[#ff3b30]'>模板输入 schema 无法解析，请联系管理员修复模板。</p>;
  }

  const order = resolveFieldOrder(normalizedSchema, uiSchema);

  return (
    <div className='space-y-4'>
      {order.map((fieldKey) => {
        const property = normalizedSchema.properties[fieldKey];
        if (!property) {
          return null;
        }

        const isRequired = Boolean(normalizedSchema.required?.includes(fieldKey));
        const label = property.title || fieldKey;
        const value = values[fieldKey] ?? property.default;

        if (property.type === 'boolean') {
          return (
            <label key={fieldKey} className='flex items-center gap-2 text-[0.9375rem] text-apple-near-black'>
              <input
                type='checkbox'
                checked={Boolean(value)}
                disabled={disabled}
                onChange={(event) => {
                  onChange({
                    ...values,
                    [fieldKey]: event.target.checked,
                  });
                }}
              />
              {label}
              {isRequired ? <span className='text-[#ff3b30]'>*</span> : null}
            </label>
          );
        }

        if (property.type === 'array') {
          const selectedValues = Array.isArray(value) ? value.map((item) => String(item)) : [];
          const enumOptions = property.enum?.map((item) => String(item)) ?? [];

          return (
            <div key={fieldKey} className='space-y-1.5'>
              <label className='block text-[0.875rem] font-medium text-apple-near-black'>
                {label}
                {isRequired ? <span className='text-[#ff3b30] ml-1'>*</span> : null}
              </label>
              {property.description ? <p className='text-[0.75rem] text-apple-gray'>{property.description}</p> : null}
              {enumOptions.length > 0 ? (
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 border border-apple-border rounded-xl p-3'>
                  {enumOptions.map((option) => (
                    <label key={option} className='inline-flex items-center gap-2 text-[0.875rem]'>
                      <input
                        type='checkbox'
                        checked={selectedValues.includes(option)}
                        disabled={disabled}
                        onChange={(event) => {
                          const nextSet = new Set(selectedValues);
                          if (event.target.checked) {
                            nextSet.add(option);
                          } else {
                            nextSet.delete(option);
                          }
                          onChange({
                            ...values,
                            [fieldKey]: Array.from(nextSet),
                          });
                        }}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  className='input min-h-24'
                  disabled={disabled}
                  value={Array.isArray(value) ? value.join(', ') : ''}
                  onChange={(event) => {
                    const next = event.target.value
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean);
                    onChange({
                      ...values,
                      [fieldKey]: next,
                    });
                  }}
                />
              )}
            </div>
          );
        }

        if (property.enum && property.enum.length > 0) {
          return (
            <div key={fieldKey} className='space-y-1.5'>
              <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={`schema-${fieldKey}`}>
                {label}
                {isRequired ? <span className='text-[#ff3b30] ml-1'>*</span> : null}
              </label>
              {property.description ? <p className='text-[0.75rem] text-apple-gray'>{property.description}</p> : null}
              <select
                id={`schema-${fieldKey}`}
                className='input'
                disabled={disabled}
                value={value === undefined ? '' : String(value)}
                onChange={(event) => {
                  onChange({
                    ...values,
                    [fieldKey]: event.target.value,
                  });
                }}
              >
                <option value=''>请选择</option>
                {property.enum.map((item) => (
                  <option key={String(item)} value={String(item)}>
                    {String(item)}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        if (property.type === 'number' || property.type === 'integer') {
          return (
            <div key={fieldKey} className='space-y-1.5'>
              <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={`schema-${fieldKey}`}>
                {label}
                {isRequired ? <span className='text-[#ff3b30] ml-1'>*</span> : null}
              </label>
              {property.description ? <p className='text-[0.75rem] text-apple-gray'>{property.description}</p> : null}
              <input
                id={`schema-${fieldKey}`}
                className='input'
                type='number'
                disabled={disabled}
                value={typeof value === 'number' ? value : ''}
                onChange={(event) => {
                  const raw = event.target.value;
                  const numeric = raw === '' ? undefined : Number(raw);
                  onChange({
                    ...values,
                    [fieldKey]: numeric,
                  });
                }}
              />
            </div>
          );
        }

        const shouldUseTextarea = fieldKey.toLowerCase().includes('description') || fieldKey.toLowerCase().includes('requirement');
        return (
          <div key={fieldKey} className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={`schema-${fieldKey}`}>
              {label}
              {isRequired ? <span className='text-[#ff3b30] ml-1'>*</span> : null}
            </label>
            {property.description ? <p className='text-[0.75rem] text-apple-gray'>{property.description}</p> : null}
            {shouldUseTextarea ? (
              <textarea
                id={`schema-${fieldKey}`}
                className='input min-h-24'
                disabled={disabled}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => {
                  onChange({
                    ...values,
                    [fieldKey]: event.target.value,
                  });
                }}
              />
            ) : (
              <input
                id={`schema-${fieldKey}`}
                className='input'
                disabled={disabled}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => {
                  onChange({
                    ...values,
                    [fieldKey]: event.target.value,
                  });
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function normalizeSchema(schema: Record<string, unknown>) {
  if (!schema || schema.type !== 'object' || typeof schema.properties !== 'object' || schema.properties == null) {
    return null;
  }

  const properties = schema.properties as Record<string, SchemaProperty>;
  const required = Array.isArray(schema.required)
    ? schema.required.filter((item): item is string => typeof item === 'string')
    : [];

  return {
    type: 'object' as const,
    properties,
    required,
  } satisfies SupportedInputSchema;
}

function resolveFieldOrder(schema: SupportedInputSchema, uiSchema?: Record<string, unknown>) {
  const fallback = Object.keys(schema.properties);

  if (!uiSchema || !Array.isArray(uiSchema.order)) {
    return fallback;
  }

  const configured = uiSchema.order.filter((item): item is string => typeof item === 'string');
  const missing = fallback.filter((key) => !configured.includes(key));
  return [...configured, ...missing];
}
